/**
 * Содержимое выдвижной панели для выбора параметров дверей и окон: тип (обычная или
 * раздвижная дверь, окно из одной, двух или трёх створок) и размеры нового проёма.
 * Дверь или окно ставят кликом по стене выбранным инструментом — или перетаскивают
 * кнопку на стену (`useCatalogCardDrag`): отпустил над стеной — проём встал там.
 */
import { CATALOG } from "@/shared/catalog";
import { UIKit } from "@/shared/ui";
import { Tool, useEditorStore } from "@/widgets/editor";
import { CatalogDragChip, useCatalogCardDrag } from "@/widgets/place-fixture";

export const OpeningsDrawer = () => {
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const openingSettings = useEditorStore((s) => s.openingSettings);
	const setOpeningSettings = useEditorStore((s) => s.setOpeningSettings);
	const startOpeningDrag = useEditorStore((s) => s.startOpeningDrag);
	const { dragProps, chip } = useCatalogCardDrag();
	const openingKind = tool === Tool.Window ? "window" : "door";
	const opening = openingSettings[openingKind];
	return (
		<div className="contents">
			<UIKit.Button
				type="button"
				className={`flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2 text-left text-sm text-ink hover:border-accent/50 hover:bg-bg${tool === Tool.Door ? " border-accent bg-accent-soft text-accent" : ""}`}
				onClick={() => setTool(Tool.Door)}
				{...dragProps(() => startOpeningDrag("door"), "Дверь")}
			>
				<UIKit.IconDoor />
				Поставить дверь
			</UIKit.Button>
			<UIKit.Button
				type="button"
				className={`flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2 text-left text-sm text-ink hover:border-accent/50 hover:bg-bg${tool === Tool.Window ? " border-accent bg-accent-soft text-accent" : ""}`}
				onClick={() => setTool(Tool.Window)}
				{...dragProps(() => startOpeningDrag("window"), "Окно")}
			>
				<UIKit.IconWindow />
				Поставить окно
			</UIKit.Button>
			{(
				[
					{ kind: "door", title: "Двери", items: CATALOG.door.items },
					{ kind: "window", title: "Окна", items: CATALOG.window.items },
				] as const
			).map(({ kind, title, items }) => (
				<div key={kind} className="flex flex-col gap-1 border-t border-border pt-2.5">
					<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
						{title}
					</UIKit.Text>
					{items.map((item) => {
						// Тип задаёт и размеры по умолчанию — их потом правят в полях ниже
						const preset = {
							item: item.id,
							width: item.width,
							height: item.height,
							sill: "sill" in item ? item.sill : 0,
						};
						const active = openingKind === kind && opening.item === item.id;
						return (
							<UIKit.Button
								key={item.id}
								type="button"
								title={item.description}
								className={`flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs text-ink hover:bg-accent-soft${active ? " border-accent bg-accent-soft font-medium text-accent" : ""}`}
								onClick={() => {
									setOpeningSettings(kind, preset);
									setTool(kind === "door" ? Tool.Door : Tool.Window);
								}}
								{...dragProps(() => {
									setOpeningSettings(kind, preset);
									startOpeningDrag(kind);
								}, item.name)}
							>
								{kind === "door" ? <UIKit.IconDoor /> : <UIKit.IconWindow />}
								<UIKit.Text as="span" className="min-w-0 flex-1">
									{item.name}
								</UIKit.Text>
							</UIKit.Button>
						);
					})}
				</div>
			))}
			<div className="flex flex-col gap-2 border-t border-border pt-2.5">
				<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
					Параметры нового проёма: {openingKind === "window" ? "окно" : "дверь"}
				</UIKit.Text>
				<UIKit.NumberField
					label="Ширина"
					value={opening.width}
					min={300}
					max={4000}
					onChange={(width) => setOpeningSettings(openingKind, { width })}
				/>
				<UIKit.NumberField
					label="Высота"
					value={opening.height}
					min={300}
					max={3000}
					onChange={(height) => setOpeningSettings(openingKind, { height })}
				/>
				{openingKind === "window" && (
					<UIKit.NumberField
						label="Подоконник"
						value={opening.sill}
						min={0}
						max={2000}
						onChange={(sill) => setOpeningSettings(openingKind, { sill })}
					/>
				)}
			</div>
			<UIKit.Text className="mt-auto text-xs text-ink">
				Выберите инструмент и кликните по стене или перетащите проём на стену — он встанет в эту
				точку. Уже поставленный проём выбирается кликом и правится в свойствах.
			</UIKit.Text>
			<CatalogDragChip chip={chip} />
		</div>
	);
};
